/* Generic key vault — fallback voor providers zonder eigen paneel. */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import { KeyRound, CheckCircle2, AlertTriangle, Send, Loader2, Webhook, Copy, Eye, EyeOff } from "lucide-react";

function GenericVault({ provider, cfg, onSaved, appBaseUrl }) {
    const { t } = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [reveal, setReveal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [testBusy, setTestBusy] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const webhookUrl = provider.webhook_info ? `${appBaseUrl}${provider.webhook_info.path}` : null;
    const copyWh = () => {
        if (!webhookUrl) return;
        copyToClipboard(webhookUrl, t("integr.webhook_copied"));
    };
    const save = async () => {
        if (!apiKey.trim()) { toast.error(t("integr.enter_key")); return; }
        setBusy(true);
        try {
            await api.patch(`/integrations/${provider.id}`, { api_key: apiKey });
            toast.success(t("integr.key_saved", { name: provider.name }));
            setApiKey(""); setReveal(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const testConnection = async () => {
        setTestBusy(true); setTestResult(null);
        try {
            const r = await api.post(`/integrations/${provider.id}/health`);
            setTestResult({ ok: r.data.ok, message: r.data.message || (r.data.ok ? t("integr.conn_ok") : t("integr.conn_fail")) });
            if (r.data.ok) toast.success(`${provider.name}: ${r.data.message || t("integr.conn_ok")}`);
            else toast.error(`${provider.name}: ${r.data.message || t("integr.conn_fail")}`);
            onSaved?.();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || t("integr.test_failed");
            setTestResult({ ok: false, message: msg });
            toast.error(msg);
        } finally { setTestBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("integr.delete_key_confirm", { name: provider.name }))) return;
        setBusy(true);
        try { await api.delete(`/integrations/${provider.id}`); toast.success(t("integr.deleted")); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("integr.delete_failed")); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{provider.key_label || "API Key"}</label>
                <div className="mt-1.5 flex gap-2">
                    <div className="flex-1 relative">
                        <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={apiKey || (cfg.api_key_masked && !reveal ? "" : "")}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder={cfg.api_key_masked || t("integr.paste_key")}
                            data-testid={`apikey-input-${provider.id}`}
                            type={reveal ? "text" : "password"}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                        <button type="button" onClick={() => setReveal((r) => !r)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>
                    <button onClick={save} disabled={busy || !apiKey.trim()} data-testid={`save-${provider.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                    </button>
                </div>
                {cfg.configured && (
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <button onClick={testConnection} disabled={testBusy}
                            data-testid={`test-connection-${provider.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-brand)]/40 bg-[var(--jr-brand)]/10 text-[var(--jr-brand)] text-xs font-medium hover:bg-[var(--jr-brand)]/20 disabled:opacity-50">
                            {testBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {t("integr.test_connection")}
                        </button>
                        <button onClick={remove} className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-[#ef4444]">
                            {t("integr.delete_current_key")}
                        </button>
                    </div>
                )}
                {testResult && (
                    <div data-testid={`test-result-${provider.id}`}
                        className={`mt-3 rounded-lg border p-3 text-xs inline-flex items-start gap-2 ${testResult.ok
                            ? "border-[#22c55e]/30 bg-[#22c55e]/[0.08] text-[#22c55e]"
                            : "border-[#ef4444]/30 bg-[#ef4444]/[0.08] text-[#ef4444]"}`}>
                        {testResult.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
                        <div className="text-[var(--jr-text-1)] leading-relaxed">
                            <strong className={testResult.ok ? "text-[#22c55e]" : "text-[#ef4444]"}>
                                {testResult.ok ? t("integr.conn_ok") : t("integr.conn_fail")}
                            </strong>{" "}
                            {testResult.message}
                        </div>
                    </div>
                )}
            </div>

            {provider.status === "placeholder" && (
                <div className="rounded-lg border border-[var(--jr-amber)]/25 bg-[var(--jr-amber)]/[0.06] p-3 text-xs text-[var(--jr-text-2)] inline-flex items-start gap-2">
                    <AlertTriangle size={14} className="text-[var(--jr-amber)] mt-0.5 shrink-0" />
                    <div>
                        <strong className="text-white">{t("integr.placeholder_title")}</strong> {t("integr.placeholder_body", { name: provider.name })}
                    </div>
                </div>
            )}

            {/* Webhook info (Surfe, etc.) */}
            {webhookUrl && (
                <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 p-3 space-y-2"
                    data-testid={`webhook-info-${provider.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                        <Webhook size={11} /> Webhook URL
                    </div>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 text-[11px] font-mono text-[var(--jr-text-2)] bg-[var(--jr-surface-2)] border border-white/10 rounded px-3 py-2 truncate">
                            {webhookUrl}
                        </code>
                        <button onClick={copyWh}
                            data-testid={`copy-webhook-${provider.id}`}
                            className="p-2 rounded border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                            <Copy size={12} />
                        </button>
                    </div>
                    <p className="text-[10px] text-[var(--jr-text-3)] leading-snug">{t(`integr.webhook_hint.${provider.id}`)}</p>
                </div>
            )}
        </div>
    );
}

export default GenericVault;
